import type { WidgetImage } from '@elara/domain/models/schemas/image-schemas';
import type { SharedItineraryEvent, SharedVenueDetails } from '@elara/domain/models/schemas/shared-itinerary-schemas';

import { getEventTypeIcon } from '@elara/client-core/lib/itinerary-utils';
import { resolveMediaUrl } from '@elara/client-core/lib/media-url';
import { getEmojiFlag } from '@elara/client-core/lib/utils';
import { createFlightSegmentModel } from '@elara/client-core/models/FlightSegmentModel';
import { ItineraryItemEventModel } from '@elara/client-core/models/ItineraryItemEventModel';

import { useCopy } from '#lib/use-copy.js';
import { AirlineLogo } from '#ui/cards/FlightCard.js';
import { ImageGalleryWidget } from '#ui/chat/ImageGalleryWidget.js';
import { SimpleMarkdown } from '#ui/chat/SimpleMarkdown.js';
import { GoogleMap } from '#ui/GoogleMap.js';
import { DestinationTips } from '#ui/guides/destination-guide.js';
import { FlightTimeline } from '#ui/itinerary/FlightTimeline.js';
import { DetailRow } from '#ui/itinerary/ItineraryDetailRow.js';
import { ItineraryDetailSummary } from '#ui/itinerary/ItineraryDetailSummary.js';
import { ItineraryLargeBlockLayout } from '#ui/itinerary/ItineraryLargeBlockLayout.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

/** The full-width, muted single-line row used for layovers. */
function CompactRow({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div
      data-print="compact-row"
      className="flex flex-col md:items-center py-8 md:py-12 px-4 md:px-8 border-b text-muted-foreground bg-background-secondary"
    >
      <div className="flex items-center gap-2 px-3 py-2.5 text-sm">
        {icon}
        <span>{title}</span>
      </div>
    </div>
  );
}

function toGalleryImages(urls: string[]): WidgetImage[] {
  return urls.map((url, index) => ({ id: `image-${index}`, url: resolveMediaUrl(url) }));
}

function directionsUrlFor(details: SharedVenueDetails): string {
  const query = details.location
    ? `${details.location.lat},${details.location.lng}`
    : [details.name, details.address].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

/**
 * Hotel or place card: photo panel beside the full description and public contact rows. Like the
 * owner's large blocks, the description is shown in full — plain text for a hotel, Markdown for a
 * place's generated description.
 */
function VenueBlock({
  event,
  model,
  details,
  title,
  preHeader,
  descriptionFormat,
}: {
  event: SharedItineraryEvent;
  model: ItineraryItemEventModel;
  details?: SharedVenueDetails;
  title: string;
  preHeader?: string;
  descriptionFormat: 'text' | 'markdown';
}) {
  const images = details ? toGalleryImages(details.images) : [];

  return (
    <ItineraryLargeBlockLayout
      icon={<MaterialIcon icon={getEventTypeIcon(event.type)} size={IconSizes.xs} />}
      typeLabel={model.formattedType}
      preHeaderContent={
        preHeader && (
          <>
            <div>·</div>
            <div className="text-xs uppercase tracking-wide">{preHeader}</div>
          </>
        )
      }
      title={title}
      panelContent={
        images.length > 0 ? (
          <ImageGalleryWidget
            images={images}
            aspect="fill"
            round="none"
            maxImages={1}
            autoplay={true}
            canAskElara={false}
          />
        ) : undefined
      }
      panelPosition="left"
    >
      {details?.description && (
        <div className="w-full max-w-prose text-pretty">
          {descriptionFormat === 'markdown' ? <SimpleMarkdown text={details.description} /> : details.description}
        </div>
      )}
      {details && (
        <ItineraryDetailSummary
          location={details.address}
          directionsUrl={directionsUrlFor(details)}
          website={details.website}
          phone={details.phone}
        />
      )}
    </ItineraryLargeBlockLayout>
  );
}

/** A shared flight's attribute rows: copyable flight number, aircraft and operator. */
function SharedFlightRows({ segment }: { segment: ReturnType<typeof createFlightSegmentModel> }) {
  const copy = useCopy();
  if (!segment.flightNumber && !segment.aircraftType && !segment.operatedBy) return null;

  return (
    <div className="flex flex-col text-sm">
      {segment.flightNumber && (
        <DetailRow
          icon="airplane_ticket"
          trailingIcon="content_copy"
          value={segment.flightNumber}
          onClick={() => copy(segment.flightNumber!, 'Flight number copied')}
          tooltip="Copy flight number"
        />
      )}
      {segment.aircraftType && <DetailRow icon="flight" value={segment.aircraftType} />}
      {segment.operatedBy && <DetailRow icon="corporate_fare" value={`Operated by ${segment.operatedBy}`} />}
    </div>
  );
}

/**
 * Read-only rendering of one shared itinerary event. Mirrors the owner's full-page blocks (same
 * layout primitives) but takes only the public projection: there are no actions, no costs, no
 * booking details and no notes — none of that data is ever sent to this page.
 */
export function SharedEventBlock({ event }: { event: SharedItineraryEvent }) {
  // The shared shape is a strict subset of `ItineraryItemEvent`, so the client event model formats
  // times and type labels exactly as on the owner's page.
  const model = ItineraryItemEventModel.from(event as Parameters<typeof ItineraryItemEventModel.from>[0]);

  switch (event.type) {
    case 'flight_departure': {
      const segment = createFlightSegmentModel(event.metadata.segment);
      return (
        <ItineraryLargeBlockLayout
          icon={<AirlineLogo airline={event.metadata.segment.airline} className="h-4 w-4" />}
          typeLabel={model.formattedType}
          title={`Flight to ${segment.destination.city_name ?? segment.destination.airport_name}`}
          panelContent={
            <GoogleMap
              points={segment.mapPoints}
              showPolyline={true}
              polylineOptions={{ strokeColor: '#d08700', strokeWeight: 2, strokeOpacity: 0.8, geodesic: true }}
              className="h-full w-full"
              interactive={false}
            />
          }
          panelPosition="left"
        >
          <FlightTimeline segment={segment} />
          <SharedFlightRows segment={segment} />
        </ItineraryLargeBlockLayout>
      );
    }

    case 'layover':
      return (
        <CompactRow
          icon={<MaterialIcon icon={getEventTypeIcon(event.type)} size={IconSizes.xs} />}
          title={event.title}
        />
      );

    case 'destination_arrival': {
      const countryCode = event.metadata.segment.destination.country_code;
      const icon = countryCode ? (
        <div className="text-xs w-4">{getEmojiFlag(countryCode)}</div>
      ) : (
        <MaterialIcon icon={getEventTypeIcon(event.type)} size={IconSizes.xs} />
      );
      const { destination } = event;
      // The owner's welcome block (`DestinationArrivalBlock`), minus its "Destination guide" action.
      const images = toGalleryImages(destination.images);
      return (
        <ItineraryLargeBlockLayout
          icon={icon}
          typeLabel={model.formattedType}
          title={destination.name}
          panelContent={
            images.length > 0 ? (
              <ImageGalleryWidget
                images={images}
                aspect="fill"
                round="none"
                maxImages={1}
                autoplay={true}
                canAskElara={false}
              />
            ) : undefined
          }
        >
          {destination.description && (
            <div className="max-w-prose">
              <SimpleMarkdown text={destination.description} />
            </div>
          )}
          {destination.logistics && (
            <div className="max-w-prose border-y print:border-y-0">
              <DestinationTips destination={{ logistics: destination.logistics }} />
            </div>
          )}
        </ItineraryLargeBlockLayout>
      );
    }

    case 'hotel_checkin':
      return (
        <VenueBlock
          event={event}
          model={model}
          details={event.details}
          title={event.details?.name ?? event.title}
          preHeader={model.originalFormattedTime ? `From ${model.originalFormattedTime}` : undefined}
          descriptionFormat="text"
        />
      );

    case 'activity':
    case 'custom':
      return (
        <ItineraryLargeBlockLayout
          icon={<MaterialIcon icon={getEventTypeIcon(event.type)} size={IconSizes.xs} />}
          typeLabel={model.formattedType}
          preHeaderContent={
            <>
              <div>·</div>
              <div className="text-xs uppercase tracking-wide">{model.formattedTime}</div>
            </>
          }
          title={event.title}
        />
      );

    default:
      return (
        <VenueBlock
          event={event}
          model={model}
          details={event.details}
          title={event.details?.name ?? event.title}
          preHeader={model.formattedTime}
          descriptionFormat="markdown"
        />
      );
  }
}
